import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { BookOpen, Search, ChevronRight } from "lucide-react";
import { PANDUAN_CHAPTERS } from "@/data/panduanContent";

// Feb 2026 — Halaman Panduan Aplikasi (READ-ONLY).
// Source of truth: /app/frontend/src/data/panduanContent.js
// (representasi 1:1 dari Buku_Panduan_Aplikasi_AROMA_NET.docx).
// Frontend-only, tidak ada endpoint/DB baru. RBAC: dapat diakses oleh
// admin/koordinator/teknisi/kolektor (semua role login).

const normalize = (s) => String(s || "").toLowerCase();

export default function Panduan() {
  const location = useLocation();
  const [query, setQuery] = useState("");
  // Chapter yang sedang dipilih (kiri = kategori, kanan = konten). Awal:
  // pertama (Pendahuluan) atau dari #hash URL.
  const [activeId, setActiveId] = useState(PANDUAN_CHAPTERS[0].id);
  // Mobile drawer state — di layar kecil sidebar dikonversi menjadi
  // dropdown yang lebih nyaman.
  const [mobileOpen, setMobileOpen] = useState(false);

  // Feb 2026 — Deep link via URL #hash (contoh /panduan#absensi).
  // Aktifkan sekali saat mount dan setiap `location.hash` berubah.
  useEffect(() => {
    const raw = (location.hash || "").replace(/^#/, "");
    if (raw && PANDUAN_CHAPTERS.some((c) => c.id === raw)) {
      setActiveId(raw);
    }
  }, [location.hash]);

  // Frontend search: match query terhadap chapter title, section heading,
  // dan item text. Non-blocking, case-insensitive, tanpa fuzzy — cukup
  // substring match yang deterministik dan bebas backend.
  const filtered = useMemo(() => {
    const q = normalize(query.trim());
    if (!q) return PANDUAN_CHAPTERS;
    return PANDUAN_CHAPTERS
      .map((c) => {
        const titleHit = normalize(c.title).includes(q);
        const sections = c.sections
          .map((sec) => {
            const headHit = normalize(sec.heading).includes(q);
            const items = sec.items.filter((it) => normalize(it).includes(q));
            if (headHit || items.length) {
              return { ...sec, items: headHit ? sec.items : items };
            }
            return null;
          })
          .filter(Boolean);
        if (titleHit || sections.length) {
          return { ...c, sections: titleHit ? c.sections : sections };
        }
        return null;
      })
      .filter(Boolean);
  }, [query]);

  // Chapter aktif — bila di-search dan chapter aktif ter-filter keluar,
  // auto-pick first match supaya panel kanan tidak kosong.
  const activeChapter = useMemo(() => {
    const found = filtered.find((c) => c.id === activeId);
    return found || filtered[0] || null;
  }, [filtered, activeId]);

  const highlight = (text) => {
    const q = query.trim();
    if (!q) return text;
    try {
      const re = new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "ig");
      const parts = String(text).split(re);
      return parts.map((p, i) =>
        re.test(p) ? (
          <mark key={i} className="bg-[#FEF3C7] text-[#78350F] rounded px-0.5">{p}</mark>
        ) : (
          <span key={i}>{p}</span>
        )
      );
    } catch {
      return text;
    }
  };

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-6" data-testid="panduan-page">
      <div>
        <div className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF]">Dokumentasi</div>
        <h1 className="font-display text-3xl md:text-4xl font-black tracking-tighter flex items-center gap-3">
          <BookOpen className="w-7 h-7 md:w-8 md:h-8 text-[#0055FF]" />
          Panduan Aplikasi
        </h1>
        <p className="text-sm text-[#6B7280] mt-1">
          Referensi operasional AROMA NET — pemakaian modul Absensi, DINAS, Rekap, Tiket, Billing, dan Notifikasi.
        </p>
      </div>

      {/* Search box */}
      <div className="rounded-lg border border-[#E5E7EB] bg-white p-3 md:p-4">
        <label className="relative block">
          <span className="sr-only">Cari di panduan</span>
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Cari di panduan..."
            className="w-full pl-9 pr-3 py-2 rounded-md border border-[#E5E7EB] focus:outline-none focus:ring-2 focus:ring-[#0055FF] text-sm"
            data-testid="panduan-search"
          />
        </label>
        {query && (
          <div className="mt-2 text-[11px] text-[#6B7280]">
            {filtered.length ? `${filtered.length} bab cocok.` : "Tidak ada hasil untuk pencarian ini."}
          </div>
        )}
      </div>

      {/* Mobile: chapter dropdown */}
      <div className="md:hidden">
        <button
          type="button"
          onClick={() => setMobileOpen((v) => !v)}
          className="w-full flex items-center justify-between px-3 py-2 rounded-md border border-[#E5E7EB] bg-white text-sm font-semibold text-[#111827]"
          data-testid="panduan-mobile-toggle"
        >
          <span>{activeChapter?.title || "Pilih Bab"}</span>
          <ChevronRight className={`w-4 h-4 transition-transform ${mobileOpen ? "rotate-90" : ""}`} />
        </button>
        {mobileOpen && (
          <div className="mt-2 rounded-md border border-[#E5E7EB] bg-white divide-y divide-[#F3F4F6]" data-testid="panduan-mobile-list">
            {filtered.map((c) => (
              <button
                key={c.id}
                onClick={() => { setActiveId(c.id); setMobileOpen(false); window.location.hash = c.id; }}
                className={`w-full text-left px-3 py-2 text-sm ${
                  activeChapter?.id === c.id ? "bg-[#EFF6FF] text-[#1D4ED8] font-semibold" : "text-[#374151]"
                }`}
              >
                {c.title}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Desktop layout: sidebar + content */}
      <div className="grid md:grid-cols-[240px_1fr] gap-4 md:gap-6">
        <aside className="hidden md:block">
          <nav className="sticky top-4 rounded-lg border border-[#E5E7EB] bg-white p-2" data-testid="panduan-sidebar">
            {filtered.map((c) => (
              <a
                key={c.id}
                href={`#${c.id}`}
                onClick={(e) => { e.preventDefault(); setActiveId(c.id); window.location.hash = c.id; }}
                data-testid={`panduan-nav-${c.id}`}
                className={`block px-3 py-2 rounded text-sm transition-colors ${
                  activeChapter?.id === c.id
                    ? "bg-[#EFF6FF] text-[#1D4ED8] font-semibold"
                    : "text-[#374151] hover:bg-[#F9FAFB]"
                }`}
              >
                {c.title}
              </a>
            ))}
            {filtered.length === 0 && (
              <div className="px-3 py-2 text-xs text-[#9CA3AF]">Tidak ada bab.</div>
            )}
          </nav>
        </aside>

        <main className="min-w-0">
          {activeChapter ? (
            <article
              id={activeChapter.id}
              data-testid={`panduan-chapter-${activeChapter.id}`}
              className="rounded-lg border border-[#E5E7EB] bg-white p-5 md:p-7 space-y-5"
            >
              <header className="border-b border-[#E5E7EB] pb-3">
                <div className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF]">Bab</div>
                <h2 className="text-2xl font-black tracking-tight text-[#111827]">{highlight(activeChapter.title)}</h2>
              </header>
              {activeChapter.sections.map((sec, i) => (
                <section key={i} className="space-y-2">
                  <h3 className="text-sm font-bold uppercase tracking-widest text-[#4B5563]">
                    {highlight(sec.heading)}
                  </h3>
                  <ul className="list-disc pl-6 space-y-1.5 text-sm text-[#1F2937] leading-relaxed">
                    {sec.items.map((it, j) => (
                      <li key={j}>{highlight(it)}</li>
                    ))}
                  </ul>
                </section>
              ))}
            </article>
          ) : (
            <div className="rounded-lg border border-[#E5E7EB] bg-white p-6 text-sm text-[#6B7280]" data-testid="panduan-empty">
              Tidak ada bab yang cocok dengan pencarian.
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
